import { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useFormik } from 'formik';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';

import { SubmitButton } from './components/SubmitButton';
import { ErrorMessage } from './components/ErrorMessage';
import { StoreSection } from './components/StoreSection';
import { BirthdateInput } from './components/BirthdateInput';
import { NameSection } from './components/NameSection';
import { AddressSection } from './components/AddressSection';
import { DividerLine } from './components/DividerLine';
import { RelocationDateInput } from './components/RelocationDateInput';
import { RelocationAddressSection } from './components/RelocationAddressSection';
import { breakpoints, initialValues } from './constants';

const Footnote = styled.div``;

interface IProps {
    className?: string;
}

const BaseApp = ({ className }: IProps) => {
    const [error, setError] = useState('');

    const formik = useFormik({
        initialValues,
        onSubmit: async (formValues) => {
            if (!process.env.REACT_APP_MAILER_API_URL) return;

            const postBody = {
                substitutionData: {
                    ...formValues,
                },
                company: 'gon',
                useEuAccount: true,
            };

            const isProduction =
                process.env.NODE_ENV === 'production' &&
                process.env.REACT_APP_IS_STAGING !== 'true';

            // Send mail to store(s) only in production build
            const storeEmail = isProduction
                ? formValues.storeEmail
                : process.env.REACT_APP_CUSTOMER_SERVICE_EMAIL;

            const storeTwoEmail = isProduction ? formValues.storeTwoEmail : '';

            const customerServiceBody = {
                ...postBody,
                email: storeEmail,
                bcc: storeTwoEmail,
                templateId: 'gon---verhuisformulier---klantenservice',
            };

            const customerBody = {
                ...postBody,
                email: formValues.customerEmail,
                templateId: 'gon---verhuisformulier---klant',
            };

            try {
                // Send customer service email
                await axios.post(
                    process.env.REACT_APP_MAILER_API_URL,
                    customerServiceBody
                );

                // Send customer email
                await axios.post(
                    process.env.REACT_APP_MAILER_API_URL,
                    customerBody
                );

                const redirectObj = window.top ?? window;
                redirectObj.location.href = `${process.env.REACT_APP_SUCCES_URL}`;
            } catch {
                setError(
                    'Er is een fout opgetreden bij het vesturen van het formulier'
                );
            }
        },
    });

    const {
        values: {
            customerEmail,
            birthdate,
            relocationDate,
            phoneNumber,
            termsAgreed,
        },
        handleChange,
        handleSubmit,
        setFieldValue,
    } = formik;

    return (
        <div className={className}>
            <form onSubmit={handleSubmit}>
                <h3>Maak je gebruik van het contactlenzenabonnement?</h3>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="sendToDeliveryService"
                            onChange={handleChange}
                        />
                    }
                    label="Ja."
                />
                <DividerLine />
                <h3>Je gegevens</h3>
                <NameSection
                    formValues={formik.values}
                    setFieldValue={setFieldValue}
                />
                <BirthdateInput
                    birthdate={birthdate}
                    setFieldValue={setFieldValue}
                />
                <TextField
                    required
                    id="phoneNumber"
                    label="Telefoonnummer"
                    value={phoneNumber}
                    onChange={handleChange}
                />
                <TextField
                    required
                    id="customerEmail"
                    label="E-mailadres"
                    type="email"
                    value={customerEmail}
                    onChange={handleChange}
                />
                <h3>Bij welke GrandOptical winkel(s) ben je klant?</h3>
                <StoreSection
                    required
                    setFieldValue={setFieldValue}
                    storeEmailField="storeEmail"
                    storeField="store"
                />
                <StoreSection
                    setFieldValue={setFieldValue}
                    storeEmailField="storeTwoEmail"
                    storeField="storeTwo"
                />
                <h3>Verhuisdatum</h3>
                <RelocationDateInput
                    relocationDate={relocationDate}
                    setFieldValue={setFieldValue}
                />
                <h3>Oud adres</h3>
                <AddressSection
                    formValues={formik.values}
                    setFieldValue={setFieldValue}
                />
                <h3>Nieuw adres</h3>
                <RelocationAddressSection
                    formValues={formik.values}
                    setFieldValue={setFieldValue}
                />
                <Footnote>* Verplicht in te vullen</Footnote>
                <DividerLine />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={termsAgreed}
                            id="termsAgreed"
                            onChange={handleChange}
                        />
                    }
                    label={
                        <>
                            Door het invullen van dit formulier ga ik akkoord
                            met de{' '}
                            <a
                                href={process.env.REACT_APP_CONDITIONS_URL}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Algemene Voorwaarden
                            </a>{' '}
                            en de{' '}
                            <a
                                href={process.env.REACT_APP_PRIVACY_URL}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Privacy Statement
                            </a>
                            .
                        </>
                    }
                />
                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : (
                    <SubmitButton disabled={!termsAgreed}>
                        VERSTUREN
                    </SubmitButton>
                )}
            </form>
        </div>
    );
};

export const App = styled(BaseApp)`
    ${Footnote} {
        font-size: 14px;
        margin-top: 20px;
    }

    // Styles matching the GNL environment
    padding: 0 32px 100px;

    @media (${breakpoints.largeMin}) {
        padding: 0 0 100px;
        margin: 0 auto;
        max-width: 920px;
    }
`;
